import { useNavigate } from "@tanstack/react-router"
import { motion } from "framer-motion"
import {
  ChevronsUpDown,
  LogOut,
  User,
} from "lucide-react"

import { useLogout } from "@/hooks/useLogout"
import { useUserStore } from "@/stores/user"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

const AVATAR_BG = "#dbeafe" // light blue bg
const AVATAR_TEXT = "#1e40af" // blue-800 text

function Avatar({ initials, size = "md" }: { initials: string; size?: "sm" | "md" }) {
  const dim = size === "sm" ? "h-9 w-9 text-xs" : "h-10 w-10 text-sm"
  return (
    <div
      className={`${dim} rounded-md flex items-center justify-center font-semibold flex-shrink-0`}
      style={{ backgroundColor: AVATAR_BG, color: AVATAR_TEXT }}
    >
      {initials}
    </div>
  )
}

export function UserProfile() {
  const { state } = useSidebar()
  const isCollapsed = state === "collapsed"
  const navigate = useNavigate()
  const logout = useLogout()
  const profile = useUserStore((s) => s.profile)

  if (!profile) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex h-14 items-center gap-2.5 rounded-md px-2.5">
            <div className="h-9 w-9 animate-pulse rounded-lg bg-slate-200" />
            {!isCollapsed && (
              <div className="flex-1 space-y-2">
                <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
                <div className="h-2.5 w-32 animate-pulse rounded bg-slate-200" />
              </div>
            )}
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu className={isCollapsed ? "items-center" : ""}>
      <SidebarMenuItem className={isCollapsed ? "flex justify-center" : ""}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className={`flex w-full items-center gap-2.5 rounded-md outline-none transition-colors hover:bg-slate-100 ${
                isCollapsed ? "justify-center p-1" : "p-2.5"
              }`}
            >
              <Avatar initials={profile.initials} />
              {!isCollapsed && (
                <>
                  <div className="grid flex-1 text-left leading-tight min-w-0">
                    <span className="truncate font-semibold text-[#0014cc]">
                      {profile.display_name}
                    </span>
                    <span className="truncate text-sm text-slate-500">
                      {profile.email}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto h-5 w-5 text-slate-400 shrink-0" />
                </>
              )}
            </motion.button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-56 rounded-lg bg-white shadow-lg"
            side="right"
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar initials={profile.initials} size="sm" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-[#0014cc]">
                    {profile.display_name}
                  </span>
                  <span className="truncate text-xs text-slate-500">
                    {profile.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => navigate({ to: "/profile" })}
              >
                <User className="mr-2 h-5 w-5" />
                Profile
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logout()}>
              <LogOut className="mr-2 h-5 w-5" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
